import { i18n } from '@quadralize/i18n'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '../../overlays/tooltip/tooltip'
import { BlockedSlotCard } from './blocked-slot-card'
import { FULL_PCT, MIN_CLICKABLE_SLOT_HEIGHT, MIN_EVENT_H, MINS_PER_HOUR } from './constants'
import { EventCard } from './event-card'
import type {
  BlockedSlot,
  BusinessHours,
  SchedulerDensity,
  SchedulerEvent,
  SchedulerProps,
  SchedulerSlotDuration,
  SchedulerView,
} from './types'
import { hourToTimeString, positionBlockedSlots, positionEvents, toEndMin, toMin } from './utils'

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SKELETON_ITEMS = [
  { id: 'sk-a', topFrac: 0.05, heightFrac: 0.14 },
  { id: 'sk-b', topFrac: 0.3, heightFrac: 0.22 },
  { id: 'sk-c', topFrac: 0.62, heightFrac: 0.1 },
] as const

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResourceColumnProps {
  columnId: string
  events: SchedulerEvent[]
  /** User-supplied blocked slots (already filtered for this column) */
  blockedSlots: BlockedSlot[]
  businessHours?: BusinessHours
  startTime: number
  endTime: number
  totalHours: number
  hourHeight: number
  pixelsPerMinute: number
  slotDuration: SchedulerSlotDuration
  /** Minimum column width in px — 150 on desktop, 100 on mobile */
  colMinWidth: number
  isLoading?: boolean
  onSlotClick?: (resourceId: string, time: string, date?: string) => void
  onEventClick?: (event: SchedulerEvent) => void
  onEventHover?: (event: SchedulerEvent | null) => void
  onBlockedSlotClick?: (slot: BlockedSlot) => void
  /** ISO date string for this column (used in week view) */
  columnDate?: string
  activeDate: Date
  view: SchedulerView
  density?: SchedulerDensity
  resource?: SchedulerProps['resources'][number]
  renderEvent?: SchedulerProps['renderEvent']
  renderSlot?: SchedulerProps['renderSlot']
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ResourceColumn = ({
  columnId,
  events,
  blockedSlots,
  businessHours,
  startTime,
  endTime,
  totalHours,
  hourHeight,
  pixelsPerMinute,
  slotDuration,
  colMinWidth,
  isLoading = false,
  onSlotClick,
  onEventClick,
  onEventHover,
  onBlockedSlotClick,
  columnDate,
  activeDate,
  view,
  density = 'comfortable',
  resource,
  renderEvent,
  renderSlot,
}: ResourceColumnProps): React.JSX.Element => {
  const { t } = useTranslation('ui', { i18n })
  const totalH = totalHours * hourHeight
  const totalMinutes = totalHours * MINS_PER_HOUR

  // Synthesize blocked slots for off-hours so they behave identically to
  // user-supplied blocked slots (no click-through, collision-aware layout)
  const offHourSlots: BlockedSlot[] = React.useMemo(() => {
    const slots: BlockedSlot[] = []
    if (businessHours) {
      if (businessHours.start > startTime) {
        slots.push({
          id: `__off-before-${columnId}`,
          startTime: hourToTimeString(startTime),
          endTime: hourToTimeString(businessHours.start),
          reason: t('scheduler.outsideHours'),
        })
      }
      if (businessHours.end < endTime) {
        slots.push({
          id: `__off-after-${columnId}`,
          startTime: hourToTimeString(businessHours.end),
          endTime: hourToTimeString(endTime),
          reason: t('scheduler.outsideHours'),
        })
      }
    }
    return slots
  }, [businessHours, startTime, endTime, columnId, t])

  // Merge off-hour slots with user-supplied ones then run collision detection
  const allBlockedSlots = React.useMemo(
    () => [...offHourSlots, ...blockedSlots],
    [offHourSlots, blockedSlots],
  )
  const positioned = React.useMemo(() => positionEvents(events), [events])
  const positionedBlocked = React.useMemo(
    () => positionBlockedSlots(allBlockedSlots),
    [allBlockedSlots],
  )

  /**
   * A slot is "free" if none of the final blocked slots (including off-hours)
   * overlap with its time range, so the click button is only rendered for
   * genuinely available times.
   */
  const isSlotBlocked = (slotTimeStr: string): boolean => {
    const slotStart = toMin(slotTimeStr)
    const slotEnd = Math.min(slotStart + slotDuration, endTime * MINS_PER_HOUR)
    return allBlockedSlots.some((b) => toMin(b.startTime) < slotEnd && toMin(b.endTime) > slotStart)
  }

  return (
    <div
      className="flex-1 border-r border-border-default last:border-r-0 relative"
      style={{ height: `${totalH}px`, minWidth: `${colMinWidth}px` }}
    >
      {/* ── Loading skeleton ── */}
      {isLoading &&
        SKELETON_ITEMS.map((s) => (
          <div
            key={s.id}
            className="absolute rounded-md bg-bg-elevated animate-pulse"
            style={{
              top: `${s.topFrac * totalH}px`,
              height: `${s.heightFrac * totalH}px`,
              left: '4px',
              right: '4px',
            }}
          />
        ))}

      {/* ── Normal content (hidden while loading) ── */}
      {!isLoading && (
        <>
          {/* Empty state — only when no events AND no user-defined blocked slots */}
          {events.length === 0 && blockedSlots.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
              <p className="text-[11px] text-text-tertiary select-none">Sem reservas</p>
            </div>
          )}

          {/* Blocked slots (includes off-hours, positioned side-by-side if overlapping) */}
          {positionedBlocked.map((bs) => (
            <BlockedSlotCard
              key={bs.id}
              slot={bs}
              startTime={startTime}
              totalHours={totalHours}
              pixelsPerMinute={pixelsPerMinute}
              onBlockedSlotClick={onBlockedSlotClick}
            />
          ))}

          {/* Clickable Empty Slots — skipped when blocked */}
          {Array.from({ length: Math.ceil(totalMinutes / slotDuration) }).map((_, slotIdx) => {
            const slotStartMinutes = startTime * MINS_PER_HOUR + slotIdx * slotDuration
            const h = Math.floor(slotStartMinutes / MINS_PER_HOUR)
            const m = slotStartMinutes % MINS_PER_HOUR
            const timeStr = hourToTimeString(h, m)
            const slotKey = `${columnId}-${timeStr}`
            const blocked = isSlotBlocked(timeStr)
            const nextSlotStart = Math.min(slotStartMinutes + slotDuration, endTime * MINS_PER_HOUR)
            const slotHeightMinutes = nextSlotStart - slotStartMinutes

            if (blocked) return null
            if (slotHeightMinutes <= 0) return null

            return (
              <Tooltip key={slotKey}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Agendar às ${timeStr}`}
                    onClick={() => onSlotClick?.(columnId, timeStr, columnDate)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSlotClick?.(columnId, timeStr, columnDate)
                      }
                    }}
                    className="group/slot absolute w-full z-10 cursor-pointer flex items-center justify-center opacity-0 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
                    style={{
                      top: `${slotIdx * slotDuration * pixelsPerMinute}px`,
                      height: `${Math.max(slotHeightMinutes * pixelsPerMinute, MIN_CLICKABLE_SLOT_HEIGHT)}px`,
                    }}
                  >
                    <span className="absolute inset-x-1 inset-y-0.5 rounded-md bg-primary/5 group-hover/slot:bg-primary/10 transition-colors ring-1 ring-inset ring-dashed ring-primary/25" />
                    <span className="relative z-10 flex items-center gap-1 text-[11px] font-semibold text-primary/80">
                      {renderSlot?.({
                        resourceId: columnId,
                        resource,
                        time: timeStr,
                        date: columnDate,
                        view,
                        activeDate,
                        isMobile: false,
                      }) ?? (
                        <>
                          <span className="text-sm leading-none">+</span>
                          {timeStr}
                        </>
                      )}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>
                    Reservar às <strong>{timeStr}</strong>
                  </p>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            )
          })}

          {/* Events */}
          {positioned.map((ev) => {
            const startMin = toMin(ev.startTime) - startTime * MINS_PER_HOUR
            const gridEndMin = totalHours * MINS_PER_HOUR
            const endMin = Math.min(
              toEndMin(ev.startTime, ev.endTime) - startTime * MINS_PER_HOUR,
              gridEndMin,
            )
            const dur = endMin - startMin
            if (startMin < 0 || startMin >= totalHours * MINS_PER_HOUR) return null

            const top = startMin * pixelsPerMinute
            const height = Math.max(dur * pixelsPerMinute, MIN_EVENT_H)
            const widthPct = FULL_PCT / ev.totalColumns
            const leftPct = ev.column * widthPct

            return (
              <EventCard
                key={ev.id}
                event={ev}
                top={top}
                height={height}
                widthPct={widthPct}
                leftPct={leftPct}
                onEventClick={onEventClick}
                onEventHover={onEventHover}
                view={view}
                activeDate={activeDate}
                resource={resource}
                isMobile={false}
                density={density}
                renderEvent={renderEvent}
              />
            )
          })}
        </>
      )}
    </div>
  )
}

ResourceColumn.displayName = 'ResourceColumn'
